/**
 * The meter and the four-criterion checklist — mockup screen 4's `.pw`, `.pw-bar`, `.pw-lb` and
 * `.pwl`.
 *
 * It renders an assessment; it does not compute one. The rule lives in `model/passwordPolicy.ts`,
 * which is also what every submit gate reads, so the checklist and the button cannot disagree.
 *
 * It lives in this slice rather than in `pages/sign-up` because it has two consumers now — the
 * sign-up credentials step and the password-reset code step — and both must draw the same four rows
 * from the same rule.
 *
 * **The whole block is hidden while the password is empty** — the resting screen is calm, per the
 * mockup's own note. The caller does not branch: this returns nothing when there is nothing to say.
 *
 * The tier is carried by the WORD ("Słabe" … "Silne"), not by the fill colour. The mockup specifies
 * a colour for the all-met state only, so every met segment takes that one confirming fill and the
 * count of filled segments plus the label do the work — which is also what keeps the meter from
 * being meaning encoded in colour alone.
 *
 * ## How the met/unmet state reaches a screen reader without inventing copy
 *
 * Sighted users read it from the glyph and the ink. Neither reaches assistive technology, and
 * "spełnione" / "niespełnione" exists in no locale bundle — and a web-only key would have to land
 * upstream in the mobile repo first. So the state is carried by ROLE instead of by words: each
 * criterion is a read-only `checkbox`, which the platform announces as checked or unchecked in the
 * user's own language, and the tier word sits in a polite live region so the summary is spoken as
 * the password is typed rather than only on demand.
 */

import { useTranslation } from 'react-i18next';

import { CheckIcon, CrossIcon } from '~/shared/ui/AuthIcons';

import type { PasswordAssessment, PasswordTier } from '../model/passwordPolicy';

import styles from './PasswordStrength.module.css';

const STRENGTH_LABEL_KEY: Record<PasswordTier, string> = {
  weak: 'auth.pw.weak',
  fair: 'auth.pw.fair',
  good: 'auth.pw.good',
  strong: 'auth.pw.strong',
};

const SEGMENTS = [0, 1, 2, 3];

interface PasswordStrengthProps {
  assessment: PasswordAssessment;
}

export function PasswordStrength({ assessment }: PasswordStrengthProps) {
  const { t } = useTranslation();

  if (assessment.strength === null) return null;

  const criteria = [
    { met: assessment.criteria.length, label: t('auth.pw.len') },
    { met: assessment.criteria.uppercase, label: t('auth.pw.upper') },
    { met: assessment.criteria.digit, label: t('auth.pw.num') },
    { met: assessment.criteria.special, label: t('auth.pw.spec') },
  ];

  return (
    <div className={assessment.strength === 'strong' ? styles.complete : undefined}>
      <div className={styles.meter}>
        {/* Decorative: the tier word beside it and the checklist below both say the same thing in
            text, and a screen reader reading four unlabelled bars would learn nothing. */}
        <div className={styles.bar} aria-hidden="true">
          {SEGMENTS.map((segment) => (
            <span
              key={segment}
              className={`${styles.segment} ${segment < assessment.metCount ? styles.segmentMet : ''}`}
            />
          ))}
        </div>
        <span className={styles.tier} role="status">
          {t(STRENGTH_LABEL_KEY[assessment.strength])}
        </span>
      </div>

      {/* Four requirements, each either met or not. Visually the state is in the glyph AND the ink;
          to assistive technology it is in the role — see the header note. */}
      <ul className={styles.criteria}>
        {criteria.map(({ met, label }) => (
          <li
            key={label}
            className={`${styles.criterion} ${met ? styles.criterionMet : ''}`}
            role="checkbox"
            aria-checked={met}
            aria-disabled="true"
          >
            {met ? (
              <CheckIcon className={styles.criterionIcon} />
            ) : (
              <CrossIcon className={styles.criterionIcon} />
            )}
            {label}
          </li>
        ))}
      </ul>
    </div>
  );
}
